import { useState } from 'react'
import { Plus, Users, Shield, Settings, Crown, Search, X, Trophy, Calendar, CheckCircle } from 'lucide-react'
import { Space, Faction } from '../types'

const mockSpaces: Space[] = [
  {
    id: '1',
    name: 'Liga Argentina de TCG',
    slug: 'liga-argentina',
    description: 'La comunidad oficial de jugadores argentinos. Torneos mensuales y eventos especiales.',
    isPublic: true,
    config: {
      maxCards: 60,
      maxCopies: 3,
      maxReserve: 15,
      allowedFactions: [Faction.HUMAN, Faction.DEMON, Faction.ELF, Faction.DRAGON, Faction.UNDEAD, Faction.NEUTRAL],
      bannedCards: [],
      bestOf: 3,
    },
    memberCount: 247,
    createdAt: '2025-06-15',
  },
  {
    id: '2',
    name: 'Torneos Online',
    slug: 'torneos-online',
    description: 'Espacio para torneos online semanales. Todos los niveles bienvenidos.',
    isPublic: true,
    config: {
      maxCards: 60,
      maxCopies: 3,
      maxReserve: 15,
      allowedFactions: [Faction.HUMAN, Faction.DEMON, Faction.ELF],
      bannedCards: [],
      bestOf: 1,
    },
    memberCount: 183,
    createdAt: '2025-08-20',
  },
  {
    id: '3',
    name: 'Casual Play',
    slug: 'casual-play',
    description: 'Para jugadores casuales que quieren disfrutar del juego sin presión competitiva.',
    isPublic: true,
    config: {
      maxCards: 50,
      maxCopies: 2,
      maxReserve: 10,
      allowedFactions: [Faction.HUMAN, Faction.ELF, Faction.NEUTRAL],
      bannedCards: [],
      bestOf: 1,
    },
    memberCount: 94,
    createdAt: '2025-10-05',
  },
]

const popularFactions = [
  { faction: Faction.HUMAN, name: 'Humanos', color: 'bg-blue-500' },
  { faction: Faction.DEMON, name: 'Demonios', color: 'bg-red-600' },
  { faction: Faction.ELF, name: 'Elfos', color: 'bg-green-500' },
  { faction: Faction.DRAGON, name: 'Dragones', color: 'bg-orange-500' },
  { faction: Faction.UNDEAD, name: 'No-Muertos', color: 'bg-gray-600' },
  { faction: Faction.NEUTRAL, name: 'Neutral', color: 'bg-slate-500' },
]

type ModalType = 'create' | 'view' | 'settings' | null

export default function SpacesPage() {
  const [search, setSearch] = useState('')
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [newSpace, setNewSpace] = useState({
    name: '',
    description: '',
    isPublic: true,
    maxCards: 60,
    maxCopies: 3,
    bestOf: 3,
  })

  const filteredSpaces = mockSpaces.filter(space =>
    space.name.toLowerCase().includes(search.toLowerCase()) ||
    space.description.toLowerCase().includes(search.toLowerCase())
  )

  const getFactionColor = (faction: Faction) => {
    switch (faction) {
      case Faction.HUMAN: return 'bg-blue-500'
      case Faction.DEMON: return 'bg-red-600'
      case Faction.ELF: return 'bg-green-500'
      case Faction.DRAGON: return 'bg-orange-500'
      case Faction.UNDEAD: return 'bg-gray-600'
      case Faction.NEUTRAL: return 'bg-slate-500'
      default: return 'bg-gray-500'
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Espacios Comunitarios</h1>
            <p className="text-slate-400">Únete a comunidades, participa en torneos y mejora tu juego</p>
          </div>
          <button
            onClick={() => setActiveModal('create')}
            className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-slate-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors"
          >
            <Plus size={20} />
            Crear Espacio
          </button>
        </div>

        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar espacios..."
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSpaces.map((space) => (
            <div
              key={space.id}
              onClick={() => {
                setSelectedSpace(space)
                setActiveModal('view')
              }}
              className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden cursor-pointer hover:border-yellow-500 hover:shadow-lg hover:shadow-yellow-500/10 transition-all duration-300"
            >
              <div className="h-24 bg-gradient-to-br from-slate-700 to-slate-800 relative">
                <div className="absolute -bottom-8 left-6">
                  <div className="w-16 h-16 bg-slate-700 rounded-xl border-4 border-slate-800 flex items-center justify-center">
                    <Crown size={28} className="text-yellow-400" />
                  </div>
                </div>
              </div>
              <div className="pt-10 p-6">
                <h3 className="text-xl font-semibold text-white mb-2">{space.name}</h3>
                <p className="text-slate-400 text-sm mb-4 line-clamp-2">{space.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {space.config.allowedFactions.slice(0, 4).map((faction) => {
                    const factionInfo = popularFactions.find(f => f.faction === faction)
                    return (
                      <span
                        key={faction}
                        className={`px-2 py-1 rounded text-xs font-medium text-white ${factionInfo?.color || 'bg-gray-500'}`}
                      >
                        {factionInfo?.name || faction}
                      </span>
                    )
                  })}
                  {space.config.allowedFactions.length > 4 && (
                    <span className="px-2 py-1 bg-slate-700 rounded text-xs text-slate-400">
                      +{space.config.allowedFactions.length - 4}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Users size={14} />
                    {space.memberCount} miembros
                  </span>
                  <span className="flex items-center gap-1">
                    <Trophy size={14} />
                    Best of {space.config.bestOf}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredSpaces.length === 0 && (
          <div className="text-center py-12">
            <Users size={48} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">No se encontraron espacios</p>
          </div>
        )}
      </div>

      {activeModal === 'create' && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-2xl max-w-lg w-full border border-slate-700 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white">Crear Nuevo Espacio</h2>
              <button
                onClick={() => setActiveModal(null)}
                className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Nombre del Espacio</label>
                <input
                  type="text"
                  value={newSpace.name}
                  onChange={(e) => setNewSpace({ ...newSpace, name: e.target.value })}
                  placeholder="Liga de tu ciudad..."
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Descripción</label>
                <textarea
                  value={newSpace.description}
                  onChange={(e) => setNewSpace({ ...newSpace, description: e.target.value })}
                  placeholder="Describe tu comunidad..."
                  rows={3}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Máximo de Cartas</label>
                  <input
                    type="number"
                    value={newSpace.maxCards}
                    onChange={(e) => setNewSpace({ ...newSpace, maxCards: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Mejor de...</label>
                  <select
                    value={newSpace.bestOf}
                    onChange={(e) => setNewSpace({ ...newSpace, bestOf: parseInt(e.target.value) })}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value={1}>1</option>
                    <option value={3}>3</option>
                    <option value={5}>5</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Máximo de Copias</label>
                <input
                  type="number"
                  value={newSpace.maxCopies}
                  onChange={(e) => setNewSpace({ ...newSpace, maxCopies: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={newSpace.isPublic}
                  onChange={(e) => setNewSpace({ ...newSpace, isPublic: e.target.checked })}
                  className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-yellow-500 focus:ring-yellow-500"
                />
                <label className="text-slate-300">Espacio público</label>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-slate-700">
              <button
                onClick={() => setActiveModal(null)}
                className="flex-1 px-4 py-3 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => setActiveModal(null)}
                className="flex-1 px-4 py-3 bg-yellow-500 text-slate-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors"
              >
                Crear Espacio
              </button>
            </div>
          </div>
        </div>
      )}

      {activeModal === 'view' && selectedSpace && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-2xl max-w-2xl w-full border border-slate-700 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="h-32 bg-gradient-to-br from-slate-700 to-slate-800 relative">
              <div className="absolute -bottom-10 left-8">
                <div className="w-20 h-20 bg-slate-700 rounded-xl border-4 border-slate-800 flex items-center justify-center">
                  <Crown size={36} className="text-yellow-400" />
                </div>
              </div>
              <button
                onClick={() => setActiveModal(null)}
                className="absolute top-4 right-4 p-2 bg-slate-700/80 rounded-lg text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="pt-12 p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedSpace.name}</h2>
                  <p className="text-slate-400">/{selectedSpace.slug}</p>
                </div>
                <button
                  onClick={() => setActiveModal('settings')}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  <Settings size={16} />
                  Configurar
                </button>
              </div>

              <p className="text-slate-300 mb-6">{selectedSpace.description}</p>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-700/30 rounded-xl p-4 text-center">
                  <Users size={24} className="mx-auto mb-2 text-blue-400" />
                  <p className="text-2xl font-bold text-white">{selectedSpace.memberCount}</p>
                  <p className="text-xs text-slate-400">Miembros</p>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4 text-center">
                  <Shield size={24} className="mx-auto mb-2 text-green-400" />
                  <p className="text-2xl font-bold text-white">{selectedSpace.config.maxCards}</p>
                  <p className="text-xs text-slate-400">Max Cartas</p>
                </div>
                <div className="bg-slate-700/30 rounded-xl p-4 text-center">
                  <Trophy size={24} className="mx-auto mb-2 text-yellow-400" />
                  <p className="text-2xl font-bold text-white">BO{selectedSpace.config.bestOf}</p>
                  <p className="text-xs text-slate-400">Formato</p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-medium text-slate-400 mb-3">Facciones Permitidas</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedSpace.config.allowedFactions.map((faction) => {
                    const factionInfo = popularFactions.find(f => f.faction === faction)
                    return (
                      <span
                        key={faction}
                        className={`px-3 py-1 rounded-full text-sm font-medium text-white ${factionInfo?.color || 'bg-gray-500'}`}
                      >
                        {factionInfo?.name || faction}
                      </span>
                    )
                  })}
                </div>
              </div>

              <div className="flex gap-3">
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 text-slate-900 font-medium rounded-lg hover:bg-yellow-400 transition-colors">
                  <CheckCircle size={18} />
                  Unirse al Espacio
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-600 transition-colors">
                  <Calendar size={18} />
                  Ver Torneos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
